import { useCallback, useState } from 'react';
import { useToggle } from 'react-use';

import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { ActionBar, ActionBarElementType, Button, ButtonVariant } from '@dreryk/react-components';

import { VisitSettlementPaymentModal } from '../../../paymentModal/VisitSettlementPaymentModal';
import { VisitSettlementPaymentModalLoader } from '../../../paymentModal/VisitSettlementPaymentModal.loader';
import { createPaymentFormBean } from '../../../shared/form/payment/payment.utils';
import { VisitDetailsFormBean } from '../../../shared/form/visitDetails/visitDetails.types';
import { VisitSettlementMedicalServicesUpdateEventInterceptor } from '../../../shared/medicalServices/VisitSettlementMedicalServicesUpdateEventInterceptor';
import { visitSettlementTranslationKeys } from '../../../translations';
import { VisitSettlementControl } from '../../../visitSettlement.types';
import { getVisitSettlementValue } from '../../../visitSettlement.utils';

import { VisitSettlementInvoiceCreateModalOpener } from './VisitSettlementInvoiceCreateModal.opener';

const t = visitSettlementTranslationKeys.extensions.visitForm.payment.action;

type VisitSettlementPaymentActionProps = {
   readonly control: VisitSettlementControl;
   readonly appointmentId: number;
   readonly practitionerId: number;
   readonly visitDetails: Omit<VisitDetailsFormBean, 'practitioner'>;
   readonly invoiceProviderIntegrationEnabled: boolean;
   readonly onSettlementModalOpen: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const VisitSettlementPaymentAction = (props: VisitSettlementPaymentActionProps): JSX.Element => {
   const [modalOpened, toggleModalOpened] = useToggle(false);
   const { editable, id: visitSettlementId } = getVisitSettlementValue(props.control);
   const [taxIdentifier, setTaxIdentifier] = useState<TaxIdentifier>();

   const handleClick = useCallback(() => {
      toggleModalOpened(true);
      props.onSettlementModalOpen();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [toggleModalOpened, props.onSettlementModalOpen]);

   return (
      <>
         <ActionBar.Portal elementType={ActionBarElementType.PRIMARY} order={-1}>
            <VisitSettlementMedicalServicesUpdateEventInterceptor
               appointmentId={props.appointmentId}
               control={props.control}
               blockingOperation
            >
               <Button
                  compact
                  testId={'settle'}
                  label={t.label}
                  variant={ButtonVariant.SECONDARY}
                  onClick={handleClick}
                  disabled={!editable}
               />
            </VisitSettlementMedicalServicesUpdateEventInterceptor>
         </ActionBar.Portal>
         <VisitSettlementInvoiceCreateModalOpener
            patientId={props.visitDetails.patient.id!}
            taxIdentifier={taxIdentifier}
            visitDate={props.visitDetails.beginDate}
            appointmentId={props.appointmentId}
            visitSettlementId={visitSettlementId}
         >
            {({ openModal: openInvoiceCreateModal }) =>
               modalOpened ? (
                  <VisitSettlementPaymentModalLoader>
                     {({ fiscalPrintEnabled }) => (
                        <VisitSettlementPaymentModal
                           title={visitSettlementTranslationKeys.paymentModal.title.settlementSummary}
                           appointmentId={props.appointmentId}
                           visitDate={props.visitDetails.beginDate}
                           practitionerId={props.practitionerId}
                           onCancel={toggleModalOpened}
                           fiscalPrintEnabled={fiscalPrintEnabled}
                           invoiceProviderIntegrationEnabled={props.invoiceProviderIntegrationEnabled}
                           onPayment={() => {
                              props.control.setValue('visitSettlement.editable', false);
                           }}
                           onSuccess={(options) => {
                              toggleModalOpened(false);

                              if (options.withInvoiceCreation) {
                                 setTaxIdentifier(options.taxIdentifierForReceipt);
                                 openInvoiceCreateModal();
                              }
                           }}
                           initializer={() => {
                              const visitSettlement = getVisitSettlementValue(props.control);

                              return {
                                 visitSettlement: {
                                    id: visitSettlement.id,
                                    medicalServices: visitSettlement.medicalServices,
                                    advancePayments: visitSettlement.advancePayments,
                                    priceChangeReason: visitSettlement.priceChangeReason,
                                    ownedByCurrentUser: true,
                                    editable: false,
                                 },
                                 payment: createPaymentFormBean({
                                    advancePayments: visitSettlement.advancePayments,
                                    medicalServices: visitSettlement.medicalServices,
                                    required: true,
                                 }),
                                 visitDetails: props.visitDetails,
                              };
                           }}
                           ownedByCurrentUser
                        />
                     )}
                  </VisitSettlementPaymentModalLoader>
               ) : null
            }
         </VisitSettlementInvoiceCreateModalOpener>
      </>
   );
};
